import React, { useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { useIntentStore, UserIntent } from '../../stores/intentStore';

/**
 * Onboarding Guard Component
 * 
 * 1. Reads intent from URL param (from home page quiz)
 * 2. Reads userType from URL param (personal/company)
 * 3. For existing users without intent: Sets a default silently
 * 4. Redirects wallet_only users to /create-pass (streamlined flow)
 * 5. Users can change mode via header dropdown
 * 
 * Risk Mitigation:
 * - wallet_only users see full generator temporarily → Redirect immediately
 * - URL params ALWAYS take priority over hydrated localStorage (prevents stale data issues)
 * 
 * Usage:
 * <OnboardingGuard>
 *   <Dashboard />
 * </OnboardingGuard>
 */

const VALID_INTENTS: UserIntent[] = [
  // New simplified intents
  'identity',
  'identity_wallet',
  'signature_only',
  'wallet_only',
  'team_identity',
  'team_full',
  'engagement',
  // Legacy support
  'personal_full',
  'personal_pass'
];

// NOTE: wallet_only users now use the existing /generator interface with only wallet tab visible
// No longer redirecting to separate /create-pass wizard - it duplicated functionality

interface OnboardingGuardProps {
  children: React.ReactNode;
  /** Skip onboarding check for this route */
  skipCheck?: boolean;
}

export const OnboardingGuard: React.FC<OnboardingGuardProps> = ({ 
  children, 
  skipCheck = false
}) => {
  const { isSignedIn, isLoaded } = useUser();
  const { intent, setIntent, setIntentFromUrl, setUserType, completeOnboarding, _hasHydrated } = useIntentStore();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Track if we've already processed URL params to avoid re-processing
  const hasProcessedUrlParams = useRef(false);
  
  // Check for intent and userType in URL params (from home page quiz)
  const urlIntent = searchParams.get('intent') as UserIntent | null;
  const urlUserType = searchParams.get('userType') as 'personal' | 'company' | null;
  
  // Effect 1: Set intent from URL params (PRIORITY - always wins over localStorage)
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    
    // URL params take absolute priority and are set immediately
    // Use setIntentFromUrl to ensure URL params beat hydrated localStorage data
    if (urlIntent && VALID_INTENTS.includes(urlIntent) && !hasProcessedUrlParams.current) {
      hasProcessedUrlParams.current = true;
      setIntentFromUrl(urlIntent);
      console.log('[OnboardingGuard] Set intent from URL param (priority):', urlIntent);
    }
    
    // Set user type if provided
    if (urlUserType && ['personal', 'company'].includes(urlUserType)) {
      setUserType(urlUserType);
      console.log('[OnboardingGuard] Set userType from URL param:', urlUserType);
    }
  }, [isLoaded, isSignedIn, urlIntent, urlUserType, setIntentFromUrl, setUserType]);
  
  // Effect 2: Set default intent ONLY after hydration completes and if no intent exists
  useEffect(() => {
    if (!isLoaded || !isSignedIn || !_hasHydrated) return;
    
    // Only set default if:
    // 1. No URL intent was provided
    // 2. No intent exists after hydration
    if (!urlIntent && intent === null) {
      setIntent('identity');
      completeOnboarding();
      console.log('[OnboardingGuard] Set default intent after hydration: identity');
    }
  }, [isLoaded, isSignedIn, _hasHydrated, urlIntent, intent, setIntent, completeOnboarding]);
  
  // wallet_only users now use the regular /generator with only wallet tab visible
  // No redirect needed - useIntentFeatures handles tab visibility
  
  // Don't check if explicitly skipped
  if (skipCheck) {
    return <>{children}</>;
  }
  
  // Wait for Clerk to load
  if (!isLoaded) {
    return null;
  }
  
  // Not signed in - let the route handle it
  if (!isSignedIn) {
    return <>{children}</>;
  }
  
  return <>{children}</>;
};

/**
 * Hook to check if onboarding is needed
 * Note: With the new approach, this mostly returns false since we auto-set defaults
 */
export const useOnboardingCheck = () => {
  const { isSignedIn, isLoaded } = useUser();
  const { intent, hasCompletedOnboarding } = useIntentStore();
  
  if (!isLoaded || !isSignedIn) {
    return { needsOnboarding: false, isLoading: !isLoaded };
  }
  
  // With auto-default behavior, onboarding is rarely "needed"
  // This hook is kept for cases where you want to explicitly check
  return {
    needsOnboarding: !hasCompletedOnboarding && intent === null,
    isLoading: false,
    currentIntent: intent
  };
};

export default OnboardingGuard;
